import React from 'react'

import {
  getTraitTranslation,
  getTraitsTranslations,
} from 'components/modals/TestsReportModal/tabs/helpers'
import { TRAITS } from 'components/common/ScoreCard/ScoreCard'

export const useTrait = ({
  factor,
  language,
  scores,
  response,
  isCandidate = undefined,
  includeNormComparison = true,
  groups,
}: {
  factor: string
  language?: string
  scores?: any
  response?: any
  isCandidate?: boolean
  includeNormComparison?: boolean
  groups?: any
}) => {
  const score = scores?.[factor]

  const {
    label,
    abstract = [],
    description = [],
  } = getTraitTranslation(
    factor,
    score,
    language,
    isCandidate,
    includeNormComparison,
  )

  const traits = TRAITS[factor]
  const traitsCount = traits.length
  const attributes = getTraitsTranslations(
    traits,
    response,
    language,
    isCandidate,
  )

  const [selectedAttributes, setSelectedAttributes] = React.useState(
    attributes.reduce((acc, attribute) => {
      return { ...acc, [attribute.id]: false }
    }, {}),
  )

  const [selectedGroups, setSelectedGroups] = React.useState(() =>
    (groups || []).reduce(
      (acc, { label: groupLabel }, index) => ({
        ...acc,
        [groupLabel]: !index,
      }),
      {},
    ),
  )

  const allScores = response.map(({ id, scores }) => ({
    id: id,
    score: Math.round(scores[factor] * 100),
  }))

  const allAttributesExpanded = attributes.every(
    attribute => selectedAttributes[attribute.id],
  )

  const allGroupsExpanded =
    !groups?.length || groups.every(group => selectedGroups[group.label])

  const allExpanded = allAttributesExpanded && allGroupsExpanded

  const selectAllAttributes = () => {
    const nextOpen = !allExpanded

    setSelectedAttributes(
      attributes.reduce(
        (acc, attribute) => ({ ...acc, [attribute.id]: nextOpen }),
        {},
      ),
    )

    if (groups?.length) {
      setSelectedGroups(
        groups.reduce(
          (acc, group) => ({ ...acc, [group.label]: nextOpen }),
          {},
        ),
      )
    }
  }

  const selectAttribute = id => isOpen =>
    setSelectedAttributes({
      ...selectedAttributes,
      [id]: isOpen,
    })

  const selectGroup = groupLabel => () =>
    setSelectedGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }))

  return {
    state: {
      abstract,
      attributes,
      selectedAttributes,
      selectedGroups,
      selectAllAttributes,
      selectAttribute,
      selectGroup,
      description,
      allExpanded,
      label,
      score,
      traits,
      traitsCount,
      allScores,
    },
  }
}
